import React, { FC, useState } from 'react';
import { AlertColor } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { UserInfo } from '../../../common/model/dto/user-info';
import { StripeUtils } from '../../../common/utils/services/stripe-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { useClinicalSnapshot } from '../../../hooks/use-clinical-snapshot';
import { useProviderDefaultStripeLink } from '../../../hooks/use-provider-default-stripe-link';
import { useAppSelector } from '../../../redux/hooks';
import { SendgridService } from '../../../services/sendgrid.service';
import OvButton from '../atoms/OvButton';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvConfirmationDialog from '../molecules/OvConfirmationDialog';
import OvSnackbar from '../molecules/OvSnackbar';
import OvClinicalSnapshot from './OvClinicalSnapshot';

interface OvPatientDetailsHeaderProps {
  patient: UserInfo;
}

const OvPatientDetailsHeader: FC<OvPatientDetailsHeaderProps> = ({
  patient,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const selectedClinicLocation = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const readPrevPathFromNavigationState = () =>
    ((location as any).state?.prevPath as string) ?? '';
  const [prevPathAtMount] = useState<string>(readPrevPathFromNavigationState);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [snackBarMessage, setSnackBarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertColor>('success');
  const [isEmailSendingLoading, setIsEmailSendingLoading] =
    useState<boolean>(false);
  const {
    data: providerDefaultStripeLink,
    isPending: isStripeLinkPending,
    isFetching: isStripeLinkFetching,
  } = useProviderDefaultStripeLink();
  const { tiles } = useClinicalSnapshot(selectedClinicLocation?.id, patient);
  const isLoading =
    isEmailSendingLoading || isStripeLinkPending || isStripeLinkFetching;

  const onBackClicked = () => {
    if (prevPathAtMount) {
      navigate(prevPathAtMount);
    } else {
      navigate(-1);
    }
  };

  const setSnackbarProps = (
    severity: AlertColor,
    message: string,
    isOpen: boolean
  ) => {
    setSnackbarSeverity(severity);
    setSnackBarMessage(message);
    setIsSnackbarOpen(isOpen);
  };

  const handleSendEmail = async () => {
    if (
      patient?.email &&
      selectedClinicLocation &&
      selectedClinicLocation?.name &&
      providerDefaultStripeLink
    ) {
      try {
        setIsEmailSendingLoading(true);
        await SendgridService.invitePatientToOrder(
          patient.email,
          `${patient.first_name} ${patient.last_name}`,
          `${currentUser?.first_name} ${currentUser?.last_name}`,
          `${providerDefaultStripeLink}?${StripeUtils.populatePaymentLinkWithData(
            currentUser,
            selectedClinicLocation,
            patient.email
          )}`,
          selectedClinicLocation.name
        );
        setIsEmailSendingLoading(false);

        setSnackbarProps(
          'success',
          t('userDetails.actions.emailSentSuccessfully'),
          true
        );
      } catch (error) {
        setIsEmailSendingLoading(false);
        setSnackbarProps(
          'error',
          t('userDetails.actions.emailSendingFailed'),
          true
        );
      } finally {
        setIsConfirmationDialogOpen(false);
      }
    }
  };

  return (
    <Wrapper>
      <ActionRow>
        <BackCta onClick={onBackClicked}>
          <StyledArrowBackIcon />
          {t('common.actions.back')}
        </BackCta>
        <InviteCta onClick={() => setIsConfirmationDialogOpen(true)}>
          <StyledShoppingCartIcon />
          {t('dashboard.shop.invitePatientToOrderCta')}
        </InviteCta>
      </ActionRow>

      <OvClinicalSnapshot
        heading={`${patient.first_name} ${patient.last_name}`}
        tiles={tiles}
      />

      <OvConfirmationDialog
        icon={<StyledShoppingBagIcon />}
        isOpen={isConfirmationDialogOpen}
        onCancel={() => setIsConfirmationDialogOpen(false)}
        onConfirm={handleSendEmail}
        title="patientDetails.actions.invitePatientToOrderDialogTitle"
        description={t(
          'patientDetails.actions.invitePatientToOrderDialogDescription',
          { patientFirstName: patient.first_name }
        )}
      />

      <OvSnackbar
        isOpen={isSnackbarOpen}
        onClose={() => setIsSnackbarOpen(false)}
        severity={snackbarSeverity}
        message={snackBarMessage}
      />

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Wrapper>
  );
};

export default OvPatientDetailsHeader;

const Wrapper = styled.header`
  margin: 0 0.5rem 1rem 0;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const BackCta = styled(OvButton)`
  && {
    padding: 0 0.625rem 0 0.25rem;
    border-radius: ${Variables.borderRadius.XLARGE};
  }
`;

const StyledArrowBackIcon = styled(ArrowBackIcon)`
  margin-right: 0.25rem;
  height: 1.25rem;
`;

const InviteCta = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    transition: none;
    font-weight: bold;
    text-transform: none;
  }
`;

const StyledShoppingCartIcon = styled(ShoppingCartIcon)`
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
`;

const StyledShoppingBagIcon = styled(ShoppingBagIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
